"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { MapPin, Truck, RefreshCw, Maximize2, Minimize2 } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { deliveryOrdersAPI } from "../services/api"

// Global flag to prevent multiple script loads
let googleMapsScriptLoading = false
let googleMapsScriptLoaded = false

interface Location {
    orderId: string
    orderNumber?: string
    status: string
    restaurant: {
        name: string
        coordinates?: { lat: number; lng: number }
    }
    customer: {
        name: string
        coordinates?: { lat: number; lng: number }
    }
    captain?: {
        name: string
        coordinates?: { lat: number; lng: number }
    }
}

interface Captain {
    _id: string
    name: string
    phone?: string
    currentLocation?: { lat: number; lng: number }
}

interface LiveOrderMapProps {
    className?: string
    onOrderClick?: (orderId: string) => void
}

const LiveOrderMap: React.FC<LiveOrderMapProps> = ({ className = "", onOrderClick }) => {
    const { t } = useLanguage()
    const mapRef = useRef<HTMLDivElement>(null)
    const googleMapRef = useRef<any>(null)
    const markersRef = useRef<any[]>([])

    const [locations, setLocations] = useState<Location[]>([])
    const [onlineCaptains, setOnlineCaptains] = useState<Captain[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [mapLoaded, setMapLoaded] = useState(false)

    const fetchLocations = useCallback(async () => {
        try {
            const response = await deliveryOrdersAPI.getActiveLocations()
            if (response.data.success) {
                const data = response.data.data

                // Transform orders to locations format
                const orders = Array.isArray(data?.orders) ? data.orders : []
                const captains = Array.isArray(data?.captains) ? data.captains : []

                const transformedLocations: Location[] = orders.map((order: any) => {
                    // Extract coordinates from delivery address
                    let customerCoords = order.deliveryAddress?.coordinates
                    if (!customerCoords && order.deliveryAddress?.lat && order.deliveryAddress?.lng) {
                        customerCoords = { lat: order.deliveryAddress.lat, lng: order.deliveryAddress.lng }
                    }

                    // Extract restaurant coordinates
                    let restaurantCoords = order.restaurant?.address?.coordinates
                    if (!restaurantCoords && order.restaurant?.address?.lat && order.restaurant?.address?.lng) {
                        restaurantCoords = { lat: order.restaurant.address.lat, lng: order.restaurant.address.lng }
                    }

                    return {
                        orderId: order._id,
                        orderNumber: order.orderNumber,
                        status: order.status,
                        restaurant: {
                            name: order.restaurant?.name || "Restaurant",
                            coordinates: restaurantCoords
                        },
                        customer: {
                            name: order.customer?.username || "Customer",
                            coordinates: customerCoords
                        },
                        captain: order.captain ? {
                            name: order.captain.name || "Driver",
                            coordinates: order.captain.currentLocation
                        } : undefined
                    }
                })

                setLocations(transformedLocations)
                setOnlineCaptains(captains)
            } else {
                setLocations([])
                setOnlineCaptains([])
            }
        } catch (err) {
            console.error("Failed to fetch locations:", err)
            setLocations([])
            setOnlineCaptains([])
            // Don't show error for empty data, only for actual failures
            if ((err as any)?.response?.status !== 404) {
                setError(t("failedToLoadMapData"))
            }
        } finally {
            setLoading(false)
        }
    }, [t])

    // Load Google Maps script
    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
        if (!apiKey) {
            setError(t("googleMapsApiKeyMissing"))
            setLoading(false)
            return
        }

        const w = window as any

        // Check if already loaded
        if (w.google?.maps?.Map) {
            googleMapsScriptLoaded = true
            setMapLoaded(true)
            return
        }

        // Check if script is already in the DOM
        const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
        if (existingScript) {
            // Script exists, wait for it to load
            if (googleMapsScriptLoaded) {
                setMapLoaded(true)
            } else {
                const checkLoaded = setInterval(() => {
                    if (w.google?.maps?.Map) {
                        googleMapsScriptLoaded = true
                        setMapLoaded(true)
                        clearInterval(checkLoaded)
                    }
                }, 100)
                // Timeout after 10 seconds
                setTimeout(() => clearInterval(checkLoaded), 10000)
            }
            return
        }

        // Prevent multiple loads
        if (googleMapsScriptLoading) {
            const checkLoaded = setInterval(() => {
                if (googleMapsScriptLoaded && w.google?.maps?.Map) {
                    setMapLoaded(true)
                    clearInterval(checkLoaded)
                }
            }, 100)
            setTimeout(() => clearInterval(checkLoaded), 10000)
            return
        }

        googleMapsScriptLoading = true

        const script = document.createElement("script")
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
        script.async = true
        script.defer = true
        script.onload = () => {
            googleMapsScriptLoaded = true
            googleMapsScriptLoading = false
            setMapLoaded(true)
        }
        script.onerror = () => {
            googleMapsScriptLoading = false
            setError(t("failedToLoadGoogleMaps"))
            setLoading(false)
        }
        document.head.appendChild(script)

        return () => {
            markersRef.current.forEach((marker: any) => {
                try { marker.setMap(null) } catch (e) { /* ignore */ }
            })
        }
    }, [t])

    // Initialize map when script is loaded
    useEffect(() => {
        if (!mapLoaded || !mapRef.current || googleMapRef.current) return

        const w = window as any

        // Check if Google Maps loaded properly
        if (!w.google?.maps?.Map) {
            console.error("Google Maps not loaded properly")
            setError(t("failedToLoadGoogleMaps"))
            return
        }

        try {
            const defaultCenter = { lat: 15.5007, lng: 32.5599 } // Khartoum, Sudan

            googleMapRef.current = new w.google.maps.Map(mapRef.current, {
                center: defaultCenter,
                zoom: 12,
                styles: [
                    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
                    { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
                ],
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            })

            fetchLocations()
        } catch (err) {
            console.error("Failed to initialize Google Maps:", err)
            setError(t("failedToLoadGoogleMaps"))
        }
    }, [mapLoaded, fetchLocations, t])

    // Update markers when locations change
    useEffect(() => {
        if (!googleMapRef.current || !mapLoaded) return

        const w = window as any

        // Safety check for Google Maps API
        if (!w.google?.maps?.Marker || !w.google?.maps?.LatLngBounds) return

        try {
            markersRef.current.forEach((marker: any) => {
                try { marker.setMap(null) } catch (e) { /* ignore */ }
            })
            markersRef.current = []

            // Safety check - ensure locations is an array
            if (!Array.isArray(locations) || locations.length === 0) return

            const bounds = new w.google.maps.LatLngBounds()
            let hasValidCoords = false

            locations.forEach((location) => {
                if (location.restaurant?.coordinates?.lat && location.restaurant?.coordinates?.lng) {
                    const restaurantMarker = new w.google.maps.Marker({
                        position: location.restaurant.coordinates,
                        map: googleMapRef.current,
                        icon: {
                            path: w.google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: "#EF4444",
                            fillOpacity: 1,
                            strokeColor: "#FFFFFF",
                            strokeWeight: 2,
                        },
                        title: location.restaurant.name,
                    })
                    restaurantMarker.addListener("click", () => onOrderClick?.(location.orderId))
                    markersRef.current.push(restaurantMarker)
                    bounds.extend(location.restaurant.coordinates)
                    hasValidCoords = true
                }

                if (location.customer?.coordinates?.lat && location.customer?.coordinates?.lng) {
                    const customerMarker = new w.google.maps.Marker({
                        position: location.customer.coordinates,
                        map: googleMapRef.current,
                        icon: {
                            path: w.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: "#3B82F6",
                            fillOpacity: 1,
                            strokeColor: "#FFFFFF",
                            strokeWeight: 2,
                        },
                        title: location.customer.name,
                    })
                    customerMarker.addListener("click", () => onOrderClick?.(location.orderId))
                    markersRef.current.push(customerMarker)
                    bounds.extend(location.customer.coordinates)
                    hasValidCoords = true
                }

                if (location.captain?.coordinates?.lat && location.captain?.coordinates?.lng) {
                    const captainMarker = new w.google.maps.Marker({
                        position: location.captain.coordinates,
                        map: googleMapRef.current,
                        icon: {
                            url: "data:image/svg+xml," + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="#10B981" stroke="#FFFFFF" stroke-width="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>`),
                            scaledSize: new w.google.maps.Size(32, 32),
                            anchor: new w.google.maps.Point(16, 16),
                        },
                        title: location.captain.name,
                    })
                    captainMarker.addListener("click", () => onOrderClick?.(location.orderId))
                    markersRef.current.push(captainMarker)
                    bounds.extend(location.captain.coordinates)
                    hasValidCoords = true
                }
            })

            if (hasValidCoords && markersRef.current.length > 0) {
                googleMapRef.current.fitBounds(bounds)
                const listener = w.google.maps.event.addListener(googleMapRef.current, "idle", () => {
                    if (googleMapRef.current?.getZoom() > 15) {
                        googleMapRef.current.setZoom(15)
                    }
                    w.google.maps.event.removeListener(listener)
                })
            }
        } catch (err) {
            console.error("Error updating map markers:", err)
        }
    }, [locations, mapLoaded, onOrderClick])

    useEffect(() => {
        const interval = setInterval(fetchLocations, 30000)
        return () => clearInterval(interval)
    }, [fetchLocations])

    const toggleFullscreen = () => setIsFullscreen(!isFullscreen)

    if (error) {
        return (
            <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
                <div className="text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">{error}</p>
                    <button onClick={fetchLocations} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                        {t("tryAgain")}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden ${isFullscreen ? "fixed inset-4 z-50" : className}`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-red-600" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">{t("liveOrderMap")}</h3>
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                        {locations.length} {t("activeOrders")}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={fetchLocations} disabled={loading} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title={t("refresh")}>
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                    </button>
                    <button onClick={toggleFullscreen} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" title={isFullscreen ? t("exitFullscreen") : t("fullscreen")}>
                        {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                    </button>
                </div>
            </div>
            <div className={`relative ${isFullscreen ? "h-[calc(100%-120px)]" : "h-[400px]"}`}>
                {loading && !mapLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                        <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}
                <div ref={mapRef} className="w-full h-full" />
            </div>
            <div className="flex items-center justify-center gap-6 p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t("restaurant")}</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t("customer")}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t("driver")} ({onlineCaptains.length} {t("online")})</span>
                </div>
            </div>
        </div>
    )
}

export default LiveOrderMap
